import { buildEvent, type DiagnosticInput, type SerializedEvent } from "./contract";
import { createQueue, type DiagnosticQueue } from "./queue";
import { deliver, type DeliverFn, type DeliveryOutcome } from "./transport";

// The explicit SPA diagnostic logger + best-effort delivery worker (ticket
// #127). Public entry point is `log(input)`, which is NON-THROWING: a producer
// calling it must never be affected by logging. Delivery is gated on the SPA
// being authenticated AND the runtime `spaDiagnostics` flag; it is in-memory,
// coalesced, single-in-flight, bounded-retry, and self-isolating (a delivery
// failure never produces another diagnostic).

export const COALESCE_MS = 250;
export const RETRY_DELAYS_MS = [1000, 2000, 4000];
const JITTER_MAX_MS = 250;

interface AuthSnapshot {
  status: "loading" | "authenticated" | "anonymous";
  profile: { sub: string } | null;
}

interface AuthStoreLike {
  getState(): AuthSnapshot;
  subscribe(listener: () => void): () => void;
}

export interface DiagnosticsDeps {
  transport?: DeliverFn;
  /** Safe, low-cardinality operational measurements; failures are ignored. */
  metrics?: DiagnosticsMetrics;
  /** One-shot CSRF recovery (default: bootstrapAuth). */
  refreshAuth?: () => Promise<unknown>;
  /** Runtime kill switch (default: the `spaDiagnostics` feature flag). */
  isEnabled?: () => boolean;
  authStore?: AuthStoreLike;
  routeProvider?: () => string | undefined;
  buildVersionProvider?: () => string | undefined;
  /** Register window visibility/pagehide/pageshow listeners (default true). */
  registerLifecycle?: boolean;
  /** Dev/test-only, rate-limited safe fallback. Never used in production. */
  devFallback?: (info: { phase: string; status?: number; dropped: number }) => void;
  now?: () => number;
  random?: () => number;
}

export interface DiagnosticsMetrics {
  increment(name: string, value?: number): void;
  observe(name: string, value: number): void;
}

export interface DiagnosticsLogger {
  log(input: DiagnosticInput): void;
  /** Test/inspection hooks. */
  queueSize(): number;
  droppedCount(): number;
  /** Best-effort lifecycle flush (also wired to visibility/pagehide). */
  flush(): void;
  dispose(): void;
}

export function createDiagnostics(deps: DiagnosticsDeps = {}): DiagnosticsLogger {
  const transport = deps.transport ?? deliver;
  const metrics = deps.metrics ?? { increment: () => {}, observe: () => {} };
  const isEnabled = deps.isEnabled ?? (() => false);
  const now = deps.now ?? Date.now;
  const random = deps.random ?? Math.random;
  const routeProvider =
    deps.routeProvider ??
    (() => (typeof window !== "undefined" ? window.location.pathname : undefined));

  const queue: DiagnosticQueue = createQueue();

  // Auth generation: bumped on logout or subject change so a queue/in-flight
  // batch can never cross identities.
  let generation = 0;
  let currentSubject: string | null = null;
  let halted = false; // set on a 401 until the next authenticated generation
  let coalesceTimer: ReturnType<typeof setTimeout> | null = null;
  let inFlight = false;
  const metricCounts = new Map<string, number>();

  const authStore = deps.authStore;
  const auth = (): AuthSnapshot =>
    authStore ? authStore.getState() : { status: "anonymous", profile: null };

  const isAuthenticated = (): boolean => auth().status === "authenticated";

  function delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function devWarn(phase: string, status?: number): void {
    if (deps.devFallback) deps.devFallback({ phase, status, dropped: queue.droppedCount() });
  }

  function incrementMetric(name: string, value = 1): void {
    metricCounts.set(name, (metricCounts.get(name) ?? 0) + value);
    try {
      metrics.increment(name, value);
    } catch {
      // Measurements are strictly best-effort and must not affect logging.
    }
  }

  function observeMetric(name: string, value: number): void {
    try {
      metrics.observe(name, value);
    } catch {
      // Measurements are strictly best-effort and must not affect logging.
    }
  }

  function metricSnapshot(): Record<string, number> {
    return Object.fromEntries(metricCounts);
  }

  function clearCoalesce(): void {
    if (coalesceTimer !== null) {
      clearTimeout(coalesceTimer);
      coalesceTimer = null;
    }
  }

  // Deliver one batch with the bounded retry/CSRF policy. Returns whether the
  // pump should drop this batch and continue, or stop entirely (queue cleared).
  async function deliverBatch(
    batch: SerializedEvent[],
    gen: number,
  ): Promise<"remove" | "stop" | "abandon"> {
    let retriesUsed = 0;
    let csrfRefreshed = false;
    for (;;) {
      if (gen !== generation) return "abandon";
      let outcome: DeliveryOutcome;
      const startedAt = now();
      try {
        incrementMetric("diagnostics.delivery.attempts");
        outcome = await transport(batch, { metrics: metricSnapshot() });
      } catch {
        outcome = { kind: "retry" }; // transport must not throw; treat as transient
      }
      observeMetric("diagnostics.delivery.latency_ms", Math.max(0, now() - startedAt));
      if (gen !== generation) return "abandon";

      switch (outcome.kind) {
        case "ack":
          incrementMetric("diagnostics.delivery.acknowledged", batch.length);
          return "remove";
        case "drop":
          incrementMetric("diagnostics.delivery.dropped", batch.length);
          devWarn("drop");
          return "remove";
        case "clear-auth":
          incrementMetric("diagnostics.delivery.auth_failures");
          queue.clear();
          halted = true;
          devWarn("clear-auth", 401);
          return "stop";
        case "csrf-refresh": {
          incrementMetric("diagnostics.delivery.csrf_refreshes");
          if (csrfRefreshed) {
            queue.clear();
            return "stop";
          }
          csrfRefreshed = true;
          try {
            await (deps.refreshAuth ? deps.refreshAuth() : Promise.resolve());
          } catch {
            queue.clear();
            return "stop";
          }
          if (gen !== generation || !isAuthenticated()) {
            queue.clear();
            return "stop";
          }
          continue; // one immediate retry
        }
        case "retry":
        case "retry-after": {
          incrementMetric("diagnostics.delivery.retries");
          if (retriesUsed >= RETRY_DELAYS_MS.length) {
            incrementMetric("diagnostics.delivery.retry_exhausted");
            devWarn("retry-exhausted");
            return "remove"; // drop this batch; later batches may still proceed
          }
          const backoff =
            outcome.kind === "retry-after"
              ? outcome.retryAfterMs
              : RETRY_DELAYS_MS[retriesUsed]! + Math.floor(random() * JITTER_MAX_MS);
          retriesUsed++;
          await delay(backoff);
          continue;
        }
      }
    }
  }

  async function pump(): Promise<void> {
    if (inFlight) return;
    inFlight = true;
    const gen = generation;
    try {
      while (gen === generation && !halted) {
        const batch = queue.peekBatch();
        if (batch.length === 0) return;
        const result = await deliverBatch(batch, gen);
        if (result === "abandon" || result === "stop") return;
        queue.drop(batch.length); // "remove"
      }
    } finally {
      inFlight = false;
    }
  }

  function scheduleDelivery(): void {
    if (coalesceTimer !== null || inFlight) return;
    coalesceTimer = setTimeout(() => {
      coalesceTimer = null;
      void pump();
    }, COALESCE_MS);
  }

  // ── auth lifecycle ───────────────────────────────────────────────
  function onAuthChange(): void {
    const snap = auth();
    const subject = snap.status === "authenticated" ? (snap.profile?.sub ?? null) : null;
    if (snap.status === "authenticated") {
      if (subject !== currentSubject) {
        // New identity (or first login): never carry a prior subject's queue.
        queue.clear();
        clearCoalesce();
        generation++;
        currentSubject = subject;
        halted = false;
      }
    } else {
      // loading or anonymous: stop and drop. A logout must not wait.
      if (currentSubject !== null || queue.size() > 0) {
        queue.clear();
        clearCoalesce();
        generation++;
      }
      currentSubject = null;
      halted = false;
    }
  }

  const unsubAuth = authStore ? authStore.subscribe(onAuthChange) : () => {};
  onAuthChange(); // seed from current state

  // ── lifecycle flush ──────────────────────────────────────────────
  function flush(): void {
    if (!isEnabled() || !isAuthenticated() || inFlight || halted) return;
    // Lifecycle flushes share the worker's single-flight guard. Otherwise a
    // pending timer can submit the same snapshot and later drop events that
    // were enqueued after the snapshot was taken.
    clearCoalesce();
    const batch = queue.peekBatch();
    if (batch.length === 0) return;
    const gen = generation;
    inFlight = true;
    // One bounded keepalive send; no retries while the document is tearing down.
    void transport(batch, { keepalive: true, metrics: metricSnapshot() })
      .then((outcome) => {
        if (gen === generation && outcome.kind === "ack") queue.drop(batch.length);
      })
      .catch(() => {})
      .finally(() => {
        inFlight = false;
        if (gen === generation && queue.size() > 0 && !halted) scheduleDelivery();
      });
  }

  const onHide = (): void => {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") flush();
  };
  const onPageHide = (): void => flush();
  const onPageShow = (): void => {
    if (queue.size() > 0) scheduleDelivery();
  };

  const lifecycleOn = deps.registerLifecycle !== false && typeof window !== "undefined";
  if (lifecycleOn) {
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);
  }

  return {
    log(input) {
      try {
        if (!isEnabled() || !isAuthenticated() || halted) return;
        const event = buildEvent(input, {
          route: routeProvider(),
          buildVersion: deps.buildVersionProvider?.(),
          occurredAt: new Date(now()).toISOString(),
        });
        if (queue.enqueue(event)) scheduleDelivery();
        else incrementMetric("diagnostics.queue.drops");
      } catch {
        // Non-throwing contract: a logging failure must never affect a producer.
      }
    },
    queueSize: () => queue.size(),
    droppedCount: () => queue.droppedCount(),
    flush,
    dispose() {
      unsubAuth();
      clearCoalesce();
      if (lifecycleOn) {
        document.removeEventListener("visibilitychange", onHide);
        window.removeEventListener("pagehide", onPageHide);
        window.removeEventListener("pageshow", onPageShow);
      }
    },
  };
}
